// client/src/components/BidEstimation/Documents/DocumentRow.js
// One dense, clickable table row for the unified Documents list. Handles both
// real documents (kind: 'document') and bid-response packages (kind:
// 'bidPackage'). Columns: Name · Status · Details(type+meta) · Actions(⋮).
//
// The whole row is clickable: it navigates to the review path; if there is
// none, it opens the file in a new tab. Every other action lives in the ⋮ menu.
import React, { useState, useRef, useEffect } from 'react'
import { MoreVert as MoreVertIcon } from '@material-ui/icons'
import ContextButton from '../shared/ContextButton'
import ContextIndicator from '../shared/ContextIndicator'
import {
  formatBytes, formatDate,
  parseStatusChip, getDocumentPageMetrics, CATEGORY_CHIP
} from './documentDisplay'

const td = { padding: '6px 8px', fontSize: 12, verticalAlign: 'middle' }

const pill = (bg, color) => ({
  display: 'inline-block', fontSize: 9, fontWeight: 600, letterSpacing: '0.03em',
  padding: '1px 7px', borderRadius: 10, whiteSpace: 'nowrap', textTransform: 'uppercase',
  background: bg, color
})

// Shared ⋮ overflow menu — `items` is [{ label, onClick, danger?, separated? }].
// The dropdown is position:fixed (anchored to the button's screen rect) so it
// escapes the scroll container's overflow clip — otherwise a menu opened on a
// bottom row gets cut off.
function OverflowMenu ({ items }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null) // { top, left } or { bottom, left }
  const btnRef = useRef(null)
  const menuRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const close = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    // Close on scroll/resize — the fixed menu would otherwise detach visually
    window.addEventListener('scroll', () => setOpen(false), true)
    window.addEventListener('resize', () => setOpen(false))
    return () => {
      document.removeEventListener('mousedown', close)
      window.removeEventListener('scroll', () => setOpen(false), true)
      window.removeEventListener('resize', () => setOpen(false))
    }
  }, [open])

  const toggle = (e) => {
    e.stopPropagation()
    if (open) { setOpen(false); return }
    const r = btnRef.current.getBoundingClientRect()
    const menuH = items.length * 34 + 8
    const below = window.innerHeight - r.bottom
    // Flip above the button if there isn't room below
    const place = below < menuH && r.top > menuH
      ? { bottom: window.innerHeight - r.top + 2, right: window.innerWidth - r.right }
      : { top: r.bottom + 2, right: window.innerWidth - r.right }
    setPos(place)
    setOpen(true)
  }

  return (
    <>
      <button
        ref={btnRef}
        className="hmi-btn hmi-btn-sm hmi-btn-ghost"
        style={{ padding: '0 4px' }}
        title="Actions"
        onClick={toggle}
      >
        <MoreVertIcon style={{ fontSize: 16, color: 'var(--ink-3)' }} />
      </button>
      {open && pos && (
        <div
          ref={menuRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', zIndex: 1500, ...pos,
            background: 'var(--bg-panel, #fff)', borderRadius: 6,
            border: '1px solid var(--line-2, #d0d0d0)',
            boxShadow: '0 4px 14px rgba(20,28,24,0.16)', minWidth: 168, padding: '4px 0',
            textAlign: 'left'
          }}
        >
          {items.map((it, i) => (
            <div
              key={i}
              onClick={(e) => { e.stopPropagation(); setOpen(false); it.onClick() }}
              style={{
                padding: '7px 14px', fontSize: 12.5, cursor: 'pointer', whiteSpace: 'nowrap',
                color: it.danger ? 'var(--risk, #c62828)' : 'inherit',
                borderTop: it.separated ? '1px solid var(--line-2, #ececec)' : undefined
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-panel-alt, #f5f5f5)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
            >
              {it.label}
            </div>
          ))}
        </div>
      )}
    </>
  )
}

const NAME_CELL = {
  ...td, maxWidth: 0, width: '46%', fontWeight: 600, color: 'var(--ink)',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
}
const rowStyle = {
  cursor: 'pointer', transition: 'background 0.1s',
  borderTop: '1px solid var(--line-2, #ececec)'
}
const hoverIn = (e) => { e.currentTarget.style.background = 'var(--bg-panel-alt, #f7f7f7)' }
const hoverOut = (e) => { e.currentTarget.style.background = 'transparent' }

/**
 * @param {object} item   normalized list item (see buildListItems in DocumentsSection)
 *   { kind:'document', id, name, category, doc }
 *   | { kind:'bidPackage', id, name, category:'bid_response', pkg }
 * @param {object} contextCount   contextCounts entry for a document (or undefined)
 * @param {string} latestJobMessage  optional pending-parse status message
 * @param {func}   onPrimaryAction / onSelectPages / onViewPages / onMapIt /
 *                 onRequestTakeoff / onDelete / onReviewBidPackage /
 *                 onDeleteBidPackage / onOpenContextDrawer
 */
export default function DocumentRow ({
  item, contextCount, latestJobMessage,
  onPrimaryAction, onSelectPages, onViewPages, onMapIt, onRequestTakeoff,
  onDelete, onReviewBidPackage, onDeleteBidPackage, onOpenContextDrawer,
  onViewImportedItems
}) {
  const catChip = CATEGORY_CHIP[item.category]
  const openInNewTab = (url) => { if (url) window.open(url, '_blank', 'noopener,noreferrer') }

  // ── Bid-package row ──────────────────────────────────────────────
  if (item.kind === 'bidPackage') {
    const pkg = item.pkg
    const sf = pkg.sourceFile || {}
    const mapped = pkg.status === 'mapped'

    // 'Open Review' = the same action as clicking the row — explicit fallback.
    const menu = [{ label: 'Open Review', onClick: () => onReviewBidPackage(pkg) }]
    if (sf.s3Url) menu.push({ label: 'Open in new tab', onClick: () => openInNewTab(sf.s3Url) })
    if (onDeleteBidPackage) {
      menu.push({ label: 'Delete', danger: true, separated: true, onClick: () => onDeleteBidPackage(pkg) })
    }

    return (
      <tr style={rowStyle} onClick={() => onReviewBidPackage(pkg)}
        onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
        <td style={NAME_CELL} title={pkg.name}>{pkg.name}</td>
        <td style={td}>
          <span style={pill(
            mapped ? 'var(--ok-tint, #e8f5e9)' : 'var(--bg-sunken, #f0f0f0)',
            mapped ? 'var(--ok, #2e7d32)' : 'var(--ink-3)'
          )}>{pkg.status}</span>
        </td>
        <td style={{ ...td, fontSize: 10.5, lineHeight: 1.25 }}>
          {catChip && (
            <div style={{ marginBottom: 2 }}>
              <span style={pill(catChip.bg, catChip.color)}>{catChip.label}</span>
            </div>
          )}
          <div style={{ color: 'var(--ink-4)' }}>
            {[
              sf.uploadedAt ? formatDate(sf.uploadedAt) : null,
              pkg.groups ? `${pkg.groups.length} group${pkg.groups.length !== 1 ? 's' : ''}` : null
            ].filter(Boolean).join(' · ')}
          </div>
        </td>
        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {contextCount && (
              <ContextIndicator
                hasBlocker={contextCount.hasBlocker}
                noteCount={contextCount.noteCount}
                hasReview={contextCount.hasReview}
              />
            )}
            <ContextButton
              parentType="document"
              parentId={pkg._id}
              parentLabel={pkg.name}
              onOpenDrawer={onOpenContextDrawer}
            />
            <OverflowMenu items={menu} />
          </span>
        </td>
      </tr>
    )
  }

  // ── Document row ─────────────────────────────────────────────────
  const doc = item.doc
  const chip = parseStatusChip(doc.parseStatus)
  const pageMetrics = getDocumentPageMetrics(doc)
  const hasPages = pageMetrics.totalPages > 0
  const isPlan = doc.category === 'plans'

  // The review path: classified plan → Select Pages; parsed → Import/Review.
  let reviewAction = null
  if (doc.parseStatus === 'classified' && hasPages) {
    reviewAction = { label: 'Select Pages', run: () => onSelectPages(doc) }
  } else if (doc.parseStatus === 'parsed') {
    const label = doc.category === 'vendor_quote' ? 'Review Quote'
      : doc.category === 'vendor_invoice' ? 'Review Invoice'
        : 'Import'
    reviewAction = { label, run: () => onPrimaryAction(doc) }
  }

  // Whole-row click: review path if there is one, else open the file.
  const onRowClick = reviewAction ? reviewAction.run : (() => openInNewTab(doc.s3Url))

  // ⋮ menu — explicit Review fallback first, then every other action.
  const menu = []
  if (reviewAction) menu.push({ label: reviewAction.label, onClick: reviewAction.run })
  if (doc.s3Url) menu.push({ label: 'Open in new tab', onClick: () => openInNewTab(doc.s3Url) })
  if (hasPages) menu.push({ label: 'View Pages', onClick: () => onViewPages(doc) })
  if (doc.parseStatus === 'parsed' && isPlan && hasPages) {
    menu.push({ label: 'Map It', onClick: () => onMapIt(doc) })
  }
  if (isPlan) menu.push({ label: 'Request Takeoff', onClick: () => onRequestTakeoff(doc) })
  if (isPlan && onViewImportedItems) {
    menu.push({ label: 'View Imported Items', onClick: () => onViewImportedItems(doc) })
  }
  if (doc.category === 'vendor_quote') {
    // Placeholder — wiring TBD. Asks the vendor to re-quote at current prices.
    menu.push({
      label: 'Request Updated Pricing',
      onClick: () => window.alert('Request Updated Pricing — coming soon.')
    })
  }
  menu.push({ label: 'Delete', danger: true, separated: true, onClick: () => onDelete(doc) })

  return (
    <tr style={rowStyle} onClick={onRowClick}
      onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
      <td style={NAME_CELL} title={doc.name}>{doc.name}</td>
      <td style={td}>
        <span style={pill(chip.style.backgroundColor, chip.style.color)}>{chip.label}</span>
      </td>
      <td style={{ ...td, fontSize: 10.5, lineHeight: 1.25 }}>
        {catChip && (
          <div style={{ marginBottom: 2 }}>
            <span style={pill(catChip.bg, catChip.color)}>{catChip.label}</span>
          </div>
        )}
        <div style={{ color: 'var(--ink-4)' }}>
          {doc.parseStatus === 'pending' && latestJobMessage ? (
            <span style={{ color: 'var(--warn, #f57f17)' }}>{latestJobMessage}</span>
          ) : (
            [
              formatBytes(doc.sizeBytes),
              formatDate(doc.uploadedAt || doc.createdAt),
              hasPages ? `${pageMetrics.totalPages} pg` : null
            ].filter(Boolean).join(' · ')
          )}
        </div>
      </td>
      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
        <span
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextCount && (
            <ContextIndicator
              hasBlocker={contextCount.hasBlocker}
              noteCount={contextCount.noteCount}
              hasReview={contextCount.hasReview}
            />
          )}
          <ContextButton
            parentType="document"
            parentId={doc._id}
            parentLabel={doc.name}
            onOpenDrawer={onOpenContextDrawer}
          />
          <OverflowMenu items={menu} />
        </span>
      </td>
    </tr>
  )
}
